import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Landmark,
  BadgeCheck,
  Activity,
  Lock,
  FileCheck,
  ArrowRight,
  ArrowUpRight,
  Umbrella,
  Vault,
  Building2,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StatusBadge } from "@/components/StatusBadge";
import { ScrollReveal } from "@/components/ScrollReveal";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { MatrixRain } from "@/components/MatrixRain";
import { LiveClock } from "@/components/LiveClock";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

function InfoDialogCard({ item, variant, t }) {
  const Icon = item.icon;
  const dark = variant === "dark";
  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          className={
            dark
              ? "shine group/card relative flex h-full w-full cursor-pointer flex-col rounded-xl border border-sidebar-border/60 bg-sidebar-accent/40 p-5 text-left backdrop-blur-sm outline-none transition-all duration-300 hover:-translate-y-1 hover:border-sidebar-primary/50 hover:bg-sidebar-accent/70 focus-visible:ring-2 focus-visible:ring-sidebar-primary"
              : "shine group/card relative flex h-full w-full cursor-pointer flex-col rounded-xl border border-border bg-card p-6 text-left outline-none transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 focus-visible:ring-2 focus-visible:ring-ring"
          }
        >
          <div
            className={
              dark
                ? "flex h-10 w-10 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground transition-transform duration-300 group-hover/card:scale-110"
                : "flex h-11 w-11 items-center justify-center rounded-md bg-primary text-accent transition-transform duration-300 group-hover/card:scale-110"
            }
          >
            <Icon className="h-5 w-5" />
          </div>
          <h3
            className={
              dark
                ? "mt-4 font-serif text-lg font-semibold text-sidebar-foreground"
                : "mt-5 font-serif text-lg font-semibold text-foreground"
            }
          >
            {t(item.titleKey)}
          </h3>
          <p
            className={
              dark
                ? "mt-1.5 text-sm leading-relaxed text-sidebar-foreground/70"
                : "mt-2 text-sm leading-relaxed text-muted-foreground"
            }
          >
            {t(item.descKey)}
          </p>
          <span
            className={
              dark
                ? "mt-4 inline-flex items-center gap-1 text-xs font-medium text-sidebar-primary"
                : "mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary"
            }
          >
            {t("ui.learnMore")}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/card:translate-x-0.5 group-hover/card:-translate-y-0.5" />
          </span>
        </div>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-accent">
            <Icon className="h-6 w-6" />
          </div>
          <DialogTitle className="mt-2 font-serif text-xl">{t(item.titleKey)}</DialogTitle>
          <DialogDescription className="leading-relaxed">{t(item.longKey)}</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border bg-secondary/50 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("ui.included")}</p>
          <ul className="mt-3 space-y-2">
            {item.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                {t(b)}
              </li>
            ))}
          </ul>
        </div>

        <Button asChild className="w-full">
          <Link to="/sign-up">
            {t("ui.requestAccess")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function LandingPage({ isAuthed = false }) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const services = [
    { icon: Vault, titleKey: "services.1.title", descKey: "services.1.desc", longKey: "services.1.long", bullets: ["services.1.b1", "services.1.b2", "services.1.b3"] },
    { icon: BadgeCheck, titleKey: "services.2.title", descKey: "services.2.desc", longKey: "services.2.long", bullets: ["services.2.b1", "services.2.b2", "services.2.b3"] },
    { icon: ShieldCheck, titleKey: "services.3.title", descKey: "services.3.desc", longKey: "services.3.long", bullets: ["services.3.b1", "services.3.b2", "services.3.b3"] },
    { icon: Activity, titleKey: "services.4.title", descKey: "services.4.desc", longKey: "services.4.long", bullets: ["services.4.b1", "services.4.b2", "services.4.b3"] },
  ];

  const insurance = [
    { icon: Umbrella, titleKey: "insurance.1.title", descKey: "insurance.1.desc", longKey: "insurance.1.long", bullets: ["insurance.1.b1", "insurance.1.b2", "insurance.1.b3"] },
    { icon: Vault, titleKey: "insurance.2.title", descKey: "insurance.2.desc", longKey: "insurance.2.long", bullets: ["insurance.2.b1", "insurance.2.b2", "insurance.2.b3"] },
    { icon: Building2, titleKey: "insurance.3.title", descKey: "insurance.3.desc", longKey: "insurance.3.long", bullets: ["insurance.3.b1", "insurance.3.b2", "insurance.3.b3"] },
    { icon: ShieldCheck, titleKey: "insurance.4.title", descKey: "insurance.4.desc", longKey: "insurance.4.long", bullets: ["insurance.4.b1", "insurance.4.b2", "insurance.4.b3"] },
  ];

  const security = [
    { icon: Landmark, title: t("security.1.title"), desc: t("security.1.desc") },
    { icon: Lock, title: t("security.2.title"), desc: t("security.2.desc") },
    { icon: ShieldCheck, title: t("security.3.title"), desc: t("security.3.desc") },
    { icon: FileCheck, title: t("security.4.title"), desc: t("security.4.desc") },
  ];

  const process = [
    { title: t("process.1.title"), desc: t("process.1.desc"), status: "pending" },
    { title: t("process.2.title"), desc: t("process.2.desc"), status: "sent" },
    { title: t("process.3.title"), desc: t("process.3.desc"), status: "released" },
  ];

  const faqs = [
    { q: "faq.1.q", a: "faq.1.a" },
    { q: "faq.2.q", a: "faq.2.a" },
    { q: "faq.3.q", a: "faq.3.a" },
    { q: "faq.4.q", a: "faq.4.a" },
    { q: "faq.5.q", a: "faq.5.a" },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader isAuthed={isAuthed} />

      <main className="flex-1">
        {/* Hero */}
        <section className="vignette relative isolate overflow-hidden bg-sidebar text-sidebar-foreground">
          <img
            src="/frankfurt-skyline.png"
            alt="Skyline des Frankfurter Bankenviertels bei Abenddämmerung"
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-sidebar/70 via-sidebar/85 to-sidebar" aria-hidden="true" />
          <MatrixRain className="opacity-70 mask-radial-fade" />
          <div className="absolute inset-0 text-sidebar-primary/25 bg-grid mask-radial-fade" aria-hidden="true" />

          <div className="relative border-b border-sidebar-border/50">
            <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
              <span className="tech-label text-sidebar-foreground/50">EST. 2009 · FRANKFURT AM MAIN</span>
              <LiveClock className="text-sidebar-foreground/60" />
            </div>
          </div>

          <div className="relative mx-auto max-w-6xl px-4 py-20 md:py-28">
            <div className="grid gap-12 lg:grid-cols-[1.35fr_0.65fr] lg:items-end">
              <div>
                <span className="tech-label inline-flex items-center gap-2 text-sidebar-primary">
                  <span className="h-px w-6 bg-sidebar-primary" />
                  {t("hero.badge")}
                </span>
                <h1 className="mt-6 text-balance font-serif text-[2.75rem] font-semibold leading-[1.02] tracking-[-0.02em] md:text-6xl lg:text-7xl">
                  {t("hero.title")}
                </h1>
                <p className="mt-7 max-w-xl text-pretty leading-relaxed text-sidebar-foreground/70 md:text-lg">
                  {t("hero.subtitle")}
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg">
                    <Link to="/sign-up">
                      {t("hero.cta.primary")}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    <a href="#insurance">{t("hero.cta.secondary")}</a>
                  </Button>
                </div>
              </div>

              <div className="lg:pb-2">
                <div className="rounded-lg border border-sidebar-border/60 bg-sidebar-accent/30 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between border-b border-sidebar-border/50 pb-3">
                    <span className="tech-label text-sidebar-foreground/50">REF · MANDAT / INSTITUTIONELL</span>
                    <ShieldCheck className="h-4 w-4 text-sidebar-primary" />
                  </div>
                  <dl className="mt-4 space-y-4">
                    <div className="flex items-end justify-between gap-3">
                      <dt className="tech-label text-sidebar-foreground/50">{t("hero.stat1")}</dt>
                      <dd className="font-serif text-xl font-semibold">
                        <AnimatedCounter value={4.2} decimals={1} prefix="€" suffix=" Mrd." />
                      </dd>
                    </div>
                    <div className="flex items-end justify-between gap-3 border-t border-sidebar-border/40 pt-4">
                      <dt className="tech-label text-sidebar-foreground/50">{t("hero.stat2")}</dt>
                      <dd className="font-serif text-xl font-semibold">
                        <AnimatedCounter value={120} suffix="+" />
                      </dd>
                    </div>
                    <div className="flex items-end justify-between gap-3 border-t border-sidebar-border/40 pt-4">
                      <dt className="tech-label text-sidebar-foreground/50">{t("hero.stat3")}</dt>
                      <dd className="font-serif text-xl font-semibold">
                        <AnimatedCounter value={99.9} decimals={1} suffix=" %" />
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-4 flex items-center gap-2 rounded-md border border-sidebar-primary/25 bg-sidebar-primary/10 px-3 py-2.5">
                    <Umbrella className="h-4 w-4 shrink-0 text-sidebar-primary" />
                    <span className="text-xs leading-snug text-sidebar-foreground/80">{t("hero.insured")}</span>
                  </div>
                  <p className="tech-label mt-3 text-sidebar-foreground/35">50.1109° N · 8.6821° E</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-b border-border bg-card">
          <div className="mx-auto max-w-6xl px-4 py-8">
            <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("trust.title")}</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {["ING", "Deutsche Bank", "Postbank", "Commerzbank", "DZ BANK"].map((name) => (
                <span key={name} className="font-serif text-lg font-semibold text-foreground/45 transition-colors duration-200 hover:text-foreground/80">
                  {name}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Insurance */}
        <section id="insurance" className="vignette relative overflow-hidden bg-sidebar text-sidebar-foreground">
          <MatrixRain className="opacity-50 mask-radial-fade" />
          <div className="absolute inset-0 text-sidebar-primary/20 bg-grid mask-radial-fade" aria-hidden="true" />
          <div className="relative mx-auto max-w-6xl px-4 py-20 md:py-28">
            <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <ScrollReveal>
                <span className="tech-label inline-flex items-center gap-2 text-sidebar-primary">
                  <span className="h-px w-6 bg-sidebar-primary" />
                  {t("insurance.badge")}
                </span>
                <h2 className="mt-6 text-balance font-serif text-3xl font-semibold tracking-tight md:text-4xl">
                  {t("insurance.title")}
                </h2>
                <p className="mt-4 max-w-xl text-pretty leading-relaxed text-sidebar-foreground/75">
                  {t("insurance.subtitle")}
                </p>
                <div className="mt-8 inline-flex flex-col rounded-xl border border-sidebar-primary/30 bg-sidebar-primary/10 px-6 py-5 backdrop-blur-sm">
                  <span className="text-xs uppercase tracking-[0.18em] text-sidebar-foreground/60">
                    {t("insurance.coverageLabel")}
                  </span>
                  <span className="mt-1 font-serif text-3xl font-semibold text-sidebar-primary md:text-4xl">
                    {t("insurance.coverageValue")}
                  </span>
                </div>
              </ScrollReveal>

              <div className="grid gap-4 sm:grid-cols-2">
                {insurance.map((item, i) => (
                  <ScrollReveal key={item.titleKey} delay={i * 80} className="h-full">
                    <InfoDialogCard item={item} variant="dark" t={t} />
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="mx-auto max-w-6xl px-4 py-20 md:py-28">
          <ScrollReveal className="max-w-2xl">
            <span className="tech-label inline-flex items-center gap-2 text-muted-foreground">
              <span className="h-px w-6 bg-accent" />
              {t("nav.services")} / 01
            </span>
            <h2 className="mt-5 text-balance font-serif text-3xl font-semibold tracking-tight md:text-4xl">
              {t("services.title")}
            </h2>
            <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">{t("services.subtitle")}</p>
          </ScrollReveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <ScrollReveal key={s.titleKey} delay={i * 80} className="h-full">
                <InfoDialogCard item={s} variant="light" t={t} />
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* Process */}
        <section id="process" className="border-y border-border bg-secondary/40">
          <div className="mx-auto max-w-6xl px-4 py-20 md:py-28">
            <ScrollReveal className="max-w-2xl">
              <h2 className="text-balance font-serif text-3xl font-semibold tracking-tight md:text-4xl">
                {t("process.title")}
              </h2>
              <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">{t("process.subtitle")}</p>
            </ScrollReveal>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("status.legend")}:
              </span>
              <StatusBadge status="sent" />
              <StatusBadge status="received" />
              <StatusBadge status="released" />
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {process.map((p, i) => (
                <ScrollReveal key={p.title} delay={i * 100} className="h-full">
                  <Card className="relative h-full border-border p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5">
                    <span className="font-serif text-4xl font-semibold text-accent">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="mt-4">
                      <StatusBadge status={p.status} />
                    </div>
                    <h3 className="mt-4 font-serif text-lg font-semibold text-foreground">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                  </Card>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Security */}
        <section id="security" className="mx-auto max-w-6xl px-4 py-20 md:py-28">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <ScrollReveal>
              <span className="tech-label inline-flex items-center gap-2 text-muted-foreground">
                <span className="h-px w-6 bg-accent" />
                Compliance / 04
              </span>
              <h2 className="mt-5 text-balance font-serif text-3xl font-semibold tracking-tight md:text-4xl">
                {t("security.title")}
              </h2>
              <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">{t("security.subtitle")}</p>
            </ScrollReveal>
            <div className="grid gap-5 sm:grid-cols-2">
              {security.map((s, i) => (
                <ScrollReveal key={s.title} delay={i * 70}>
                  <div className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-secondary text-primary transition-colors duration-300 hover:bg-primary hover:text-accent">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">{s.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="border-y border-border bg-secondary/40">
          <div className="mx-auto max-w-3xl px-4 py-20 md:py-28">
            <ScrollReveal className="text-center">
              <span className="tech-label inline-flex items-center justify-center gap-2 text-muted-foreground">
                <HelpCircle className="h-3.5 w-3.5" />
                FAQ / INSTITUTIONEN
              </span>
              <h2 className="mt-5 text-balance font-serif text-3xl font-semibold tracking-tight md:text-4xl">
                {t("faq.title")}
              </h2>
              <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">{t("faq.subtitle")}</p>
            </ScrollReveal>

            <ScrollReveal className="mt-10">
              <Accordion type="single" collapsible className="rounded-xl border border-border bg-card px-5">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`item-${i}`}>
                    <AccordionTrigger className="py-5 text-left font-serif text-base font-medium text-foreground">
                      {t(f.q)}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      {t(f.a)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="relative isolate overflow-hidden bg-sidebar text-sidebar-foreground">
          <img src="/frankfurt-skyline.png" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-sidebar/85 via-sidebar/92 to-sidebar" aria-hidden="true" />
          <div className="relative mx-auto max-w-4xl px-4 py-20 text-center md:py-24">
            <ScrollReveal>
              <h2 className="text-balance font-serif text-3xl font-semibold tracking-tight md:text-4xl">
                {t("cta.title")}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-pretty leading-relaxed text-sidebar-foreground/75">
                {t("cta.subtitle")}
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-sidebar-foreground/80">
                <CheckCircle2 className="h-4 w-4 text-sidebar-primary" />
                {t("hero.insured")}
              </div>
              <div className="mt-8 flex justify-center">
                <Button size="lg" onClick={() => navigate("/sign-up")}>
                  {t("cta.button")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-4 text-xs text-sidebar-foreground/55">{t("cta.note")}</p>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

export default LandingPage;
